// KategorisierungPage.tsx
// Categorization workbench: Linear-style master-detail.
//   Left  : virtualized list of uncategorized articles (per supplier).
//   Right : sticky context panel with category dropdown + bio toggle + save.
//
// AI suggestion (P2) and bulk operations (P2 polish) come in a later loop.

import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../../../shared/ui'
import { Dropdown, type DropdownOption } from '../../../../shared/ui/Dropdown'
import { Button } from '../../../../shared/ui/Button'
import {
  fetchProductCategories,
  fetchUncategorizedArticles,
  createSupplierProductMapping,
  suggestProductCategory,
  type UncategorizedArticle,
} from '../api/categorizationApi'

function formatEur(s: string | number | null | undefined): string {
  const n = typeof s === 'string' ? parseFloat(s) : (s ?? 0)
  if (isNaN(n)) return '0,00 €'
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

function formatDateDe(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function KategorisierungPage() {
  const { supplierId = 'chefsculinar' } = useParams<{ supplierId: string }>()
  const queryClient = useQueryClient()

  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [pickedCategoryId, setPickedCategoryId] = useState<string>('')
  const [pickedBio, setPickedBio] = useState<boolean>(false)
  const [toast, setToast] = useState<string | null>(null)

  const categoriesQuery = useQuery({
    queryKey: ['product-categories'],
    queryFn: fetchProductCategories,
    staleTime: 5 * 60_000,
  })

  const articlesQuery = useQuery({
    queryKey: ['uncategorized-articles', supplierId],
    queryFn: () => fetchUncategorizedArticles(supplierId),
    staleTime: 30_000,
  })

  const articles = articlesQuery.data ?? []
  const selected: UncategorizedArticle | undefined = useMemo(
    () => articles.find(a => a.articleKey === selectedKey),
    [articles, selectedKey],
  )

  // Reset detail selection when supplier changes / article disappears
  useEffect(() => {
    if (selectedKey && !articles.some(a => a.articleKey === selectedKey)) {
      setSelectedKey(null)
      setPickedCategoryId('')
      setPickedBio(false)
    }
  }, [articles, selectedKey])

  // Pre-fill detail-pane defaults when selecting a different article
  useEffect(() => {
    if (!selected) return
    setPickedCategoryId('')
    setPickedBio(false)
  }, [selected?.articleKey])

  // AI category suggestion — fetched per selected article
  const suggestionQuery = useQuery({
    queryKey: ['ai-category-suggestion', selected?.articleKey],
    queryFn: () => suggestProductCategory(selected!.itemName),
    enabled: !!selected,
    staleTime: 5 * 60_000,
  })

  // Pre-fill from top suggestion (only when user hasn't picked anything yet)
  useEffect(() => {
    if (!suggestionQuery.data || pickedCategoryId) return
    const top = suggestionQuery.data.candidates[0]
    if (!top) return
    const cat = (categoriesQuery.data ?? []).find(c => c.code === top.categoryCode)
    if (cat) setPickedCategoryId(String(cat.id))
    if (suggestionQuery.data.bioFlag.value) setPickedBio(true)
  }, [suggestionQuery.data, categoriesQuery.data, pickedCategoryId])

  const categoryOptions: DropdownOption[] = (categoriesQuery.data ?? []).map(c => ({
    value: String(c.id),
    label: c.label,
    description: c.code,
  }))

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selected || !pickedCategoryId) throw new Error('Kategorie auswählen')
      const cat = (categoriesQuery.data ?? []).find(c => String(c.id) === pickedCategoryId)
      const bioFlag = cat?.bioDefault === true ? true : pickedBio
      return createSupplierProductMapping(supplierId, {
        supplierArticleKey: selected.supplierArticleKey ?? selected.articleKey,
        supplierArticleIdRaw: selected.supplierArticleKey?.startsWith('ARTICLE:')
          ? selected.supplierArticleKey.slice('ARTICLE:'.length)
          : null,
        itemNameNorm: selected.itemName.trim().toLowerCase(),
        productCategoryId: Number(pickedCategoryId),
        bioFlag,
        source: 'user_confirmed',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uncategorized-articles', supplierId] })
      setToast('Zuordnung gespeichert.')
      setTimeout(() => setToast(null), 2500)
      // Auto-advance to next article
      const i = articles.findIndex(a => a.articleKey === selectedKey)
      const next = i >= 0 ? articles[i + 1] : null
      setSelectedKey(next?.articleKey ?? null)
    },
    onError: (e: Error) => {
      setToast(`Fehler: ${e.message}`)
      setTimeout(() => setToast(null), 4000)
    },
  })

  return (
    <div className="px-6 py-6 space-y-5">
      <PageHeader
        title={`Produktkategorisierung — ${supplierId}`}
        subtitle="Ordne unkategorisierte Artikel einer Hauptkategorie zu. Änderungen wirken rückwirkend auf alle vorhandenen Rechnungen dieses Lieferanten."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] gap-4">

        {/* ── Master list ──────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-outline-variant/40 bg-surface-container-lowest overflow-hidden">
          <div className="px-4 py-3 border-b border-outline-variant/30 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-on-surface">Unkategorisierte Artikel</p>
              <p className="text-xs text-on-surface-variant tabular-nums">
                {articlesQuery.isLoading ? 'lädt…' : `${articles.length.toLocaleString('de-DE')} Artikel`}
              </p>
            </div>
          </div>

          {articlesQuery.isLoading ? (
            <div className="px-4 py-10 text-center text-sm text-on-surface-variant">Lädt…</div>
          ) : articlesQuery.isError ? (
            <div className="px-4 py-10 text-center text-sm text-error">
              Konnte Artikel nicht laden. Bitte später erneut versuchen.
            </div>
          ) : articles.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-on-surface-variant">
              Keine unkategorisierten Artikel — alles zugeordnet.
            </div>
          ) : (
            <ul className="max-h-[70vh] overflow-y-auto divide-y divide-outline-variant/15">
              {articles.map(a => {
                const active = a.articleKey === selectedKey
                return (
                  <li
                    key={a.articleKey}
                    onClick={() => setSelectedKey(a.articleKey)}
                    className={[
                      'px-4 py-3 cursor-pointer transition-colors flex items-center gap-3',
                      active
                        ? 'bg-primary/10 border-l-2 border-l-primary'
                        : 'hover:bg-surface-container-low',
                    ].join(' ')}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-on-surface truncate" title={a.itemName}>
                        {a.itemName}
                      </p>
                      <p className="text-[11px] text-on-surface-variant tabular-nums">
                        {a.lineCount.toLocaleString('de-DE')} Zeilen · {a.invoiceCount.toLocaleString('de-DE')} Rechnungen · {formatDateDe(a.firstSeen)} – {formatDateDe(a.lastSeen)}
                      </p>
                      {a.supplierArticleKey && (
                        <p className="text-[10px] font-mono text-on-surface-variant/70 truncate">{a.supplierArticleKey}</p>
                      )}
                    </div>
                    <span className="text-sm font-bold tabular-nums text-on-surface shrink-0">
                      {formatEur(a.totalNet)}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* ── Detail pane ──────────────────────────────────────────────── */}
        <div className="rounded-2xl border border-outline-variant/40 bg-surface-container/85 backdrop-blur-md overflow-hidden h-fit lg:sticky lg:top-4">
          {!selected ? (
            <div className="px-5 py-12 text-center text-sm text-on-surface-variant">
              Wähle einen Artikel links, um eine Kategorie zuzuweisen.
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold text-on-surface-variant">Artikel</p>
                <p className="text-base font-bold text-on-surface mt-1 break-words">{selected.itemName}</p>
                {selected.supplierArticleKey && (
                  <p className="text-[11px] font-mono text-on-surface-variant mt-0.5">{selected.supplierArticleKey}</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-surface-container-lowest p-2">
                  <p className="text-[10px] uppercase tracking-widest text-on-surface-variant">Zeilen</p>
                  <p className="text-sm font-bold text-on-surface tabular-nums">{selected.lineCount.toLocaleString('de-DE')}</p>
                </div>
                <div className="rounded-lg bg-surface-container-lowest p-2">
                  <p className="text-[10px] uppercase tracking-widest text-on-surface-variant">Rechnungen</p>
                  <p className="text-sm font-bold text-on-surface tabular-nums">{selected.invoiceCount.toLocaleString('de-DE')}</p>
                </div>
                <div className="rounded-lg bg-surface-container-lowest p-2">
                  <p className="text-[10px] uppercase tracking-widest text-on-surface-variant">Umsatz</p>
                  <p className="text-sm font-bold text-on-surface tabular-nums">{formatEur(selected.totalNet)}</p>
                </div>
              </div>

              {/* AI suggestion chips — dashed border = candidate; click to apply */}
              {suggestionQuery.data && suggestionQuery.data.candidates.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-on-surface-variant mb-1.5 inline-flex items-center gap-1">
                    <span className="material-symbols-outlined !text-[12px] text-primary" aria-hidden="true">auto_awesome</span>
                    Vorschläge
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestionQuery.data.candidates.map((c) => {
                      const cat = (categoriesQuery.data ?? []).find(x => x.code === c.categoryCode)
                      if (!cat) return null
                      const active = String(cat.id) === pickedCategoryId
                      return (
                        <button
                          key={c.categoryCode}
                          type="button"
                          onClick={() => setPickedCategoryId(String(cat.id))}
                          title={c.reason}
                          className={[
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
                            active
                              ? 'bg-primary text-on-primary border border-primary'
                              : 'bg-primary/5 text-on-surface border border-dashed border-primary/40 hover:bg-primary/10',
                          ].join(' ')}
                        >
                          {cat.label}
                          <span className="text-[9px] opacity-70 tabular-nums">{Math.round(c.confidence * 100)}%</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-on-surface-variant mb-1.5">
                  Kategorie
                </label>
                <Dropdown
                  value={pickedCategoryId}
                  options={categoryOptions}
                  onChange={(v) => setPickedCategoryId(v)}
                  placeholder="Kategorie wählen…"
                  searchable
                  usePortal
                />
              </div>

              <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-surface-container-lowest border border-outline-variant/30 cursor-pointer hover:border-primary/40 transition-colors">
                <input
                  type="checkbox"
                  checked={pickedBio}
                  onChange={(e) => setPickedBio(e.target.checked)}
                  className="size-4 accent-primary"
                />
                <span className="text-sm text-on-surface flex-1">Bio-Produkt</span>
                <span className="text-[10px] text-on-surface-variant">Zählt zur Bio-Quote</span>
              </label>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="primary"
                  icon="check_circle"
                  disabled={!pickedCategoryId || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {saveMutation.isPending ? 'Speichert…' : 'Speichern & weiter'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedKey(null)}
                >
                  Zurück
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div role="status" aria-live="polite" className="fixed bottom-6 right-6 z-50 rounded-xl border border-outline-variant/40 bg-surface-container/95 backdrop-blur-md px-4 py-3 shadow-lg text-sm text-on-surface motion-safe:animate-[fade-in-up_0.3s_ease-out]">
          {toast}
        </div>
      )}
    </div>
  )
}
